import type { ComponentType, ReactNode, SVGProps } from 'react';
import { cn } from '../../utils/cn';

type IconType = ComponentType<SVGProps<SVGSVGElement>>;

type SettingsSectionProps = {
  id?: string;
  icon?: IconType;
  title: string;
  description?: string;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
  featured?: boolean;
};

export default function SettingsSection({
  id,
  icon: Icon,
  title,
  description,
  actions,
  children,
  className,
  bodyClassName,
  featured = false,
}: SettingsSectionProps) {
  return (
    <section
      id={id}
      className={cn(
        'rounded-2xl border shadow-sm overflow-hidden',
        featured
          ? 'border-app-accent/25 bg-gradient-to-br from-app-surface via-app-surface to-app-accent/5'
          : 'border-app-border bg-app-surface',
        className,
      )}
    >
      <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between px-5 py-4 border-b border-app-border/80 bg-app-bg/30">
        <div className="flex items-start gap-3 min-w-0">
          {Icon && (
            <div
              className={cn(
                'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
                featured ? 'bg-app-accent/15 text-app-accent' : 'bg-app-bg text-app-text-dim',
              )}
            >
              <Icon className="h-5 w-5" aria-hidden />
            </div>
          )}
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-app-text tracking-tight">{title}</h2>
            {description && (
              <p className="mt-1 text-xs text-app-text-sec leading-relaxed max-w-prose">{description}</p>
            )}
          </div>
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2 shrink-0">{actions}</div>}
      </header>
      <div className={cn('px-5 py-5', bodyClassName)}>{children}</div>
    </section>
  );
}
